import React from 'react';
import ReactDOM from 'react-dom/client';
import { MsalProvider } from '@azure/msal-react';
import { BrowserRouter } from 'react-router';
import { apiMode } from './api/mode';
import { getMsalInstance } from './auth/msal';
import { canonicalOrigin, movedTarget, PENDING_CHECK_TIMEOUT_MS, setPendingMove } from './canonicalHost';
import { assertConfigured } from './config';
import { getSyncEngine, getTaskOutbox } from './local';
import App from './App';
import './styles.css';

// The old address forwards to the custom domain (src/canonicalHost.ts) —
// unless this browser still holds unsynced audit work here: then the app
// starts as usual with a banner, and moves once that work has synced.
const moveTo = movedTarget(window.location, canonicalOrigin());
if (moveTo) {
  // A store that can't be read, or doesn't answer, counts as "may hold work".
  const timeout = new Promise<null>((resolve) => setTimeout(() => resolve(null), PENDING_CHECK_TIMEOUT_MS));
  // Task comments/files waiting on this device (B4) count too.
  const pendingAll = Promise.all([getSyncEngine().pendingItems(), getTaskOutbox().pendingItems()]).then(([a, t]) =>
    a === null || t === null ? null : a + t,
  );
  void Promise.race([pendingAll, timeout]).then((pending) => {
    if (pending === 0) window.location.replace(moveTo);
    else {
      setPendingMove({ target: moveTo, unknown: pending === null });
      start();
    }
  });
} else {
  start();
}

function start(): void {
  const root = ReactDOM.createRoot(document.getElementById('root')!);

  // Read ?api=1 / ?api=0 before any sign-in redirect can drop the query string.
  // The resulting data path is also marked on the page (invisible) so the E2E
  // harness can prove server mode after cutover, when no chip shows.
  document.documentElement.dataset.dataPath = apiMode() ?? 'direct';

  // Wake the server NOW, alongside sign-in, so an idle one (cold start) is
  // ready by the time /api/me is asked (api/src/functions/ping.ts).
  if (apiMode()) void fetch('/api/ping', { cache: 'no-store' }).catch(() => undefined);

  const configError = assertConfigured();
  if (configError) {
    root.render(
      <div className="config-error">
        <h1>SSP AuditPlus</h1>
        <p>{configError}</p>
      </div>,
    );
  } else {
    // handleRedirectPromise must settle before rendering so a returning
    // sign-in redirect lands with its account already active.
    const msalInstance = getMsalInstance();
    void msalInstance.initialize().then(async () => {
      try {
        await msalInstance.handleRedirectPromise();
      } catch (err) {
        // A cancelled or CA-blocked sign-in redirect rejects here. Render the
        // app anyway so the user lands back on the sign-in page and can retry,
        // instead of a blank screen.
        console.error('Sign-in redirect failed', err);
      }
      performance.mark('auditplus:sign-in-ready');
      root.render(
        <React.StrictMode>
          <MsalProvider instance={msalInstance}>
            <BrowserRouter>
              <App />
            </BrowserRouter>
          </MsalProvider>
        </React.StrictMode>,
      );
    });
  }
}
